import type {
  AllNodesValidationStates,
  NodesValidationRequest,
  NodesValidationResponse,
  WorkerCacheConfig,
} from "@features/builder/graph/nodes/validation/types"
import { validateNodeExpression } from "@features/builder/graph/nodes/validation/validationOnMainThread/validateNodeExpression.mainThread"
import { useSkipValidation } from "@features/builder/graph/nodes/validation/useSkipValidation"
import type { AppNode } from "@features/builder/graph/types"
import { EditorGraphDto, GraphDetails } from "@features/builder/types"
import { mergeNodesValidationAtom } from "@features/builder/graph/nodes/validation/validationAtom"
import { getNormalisedGraphKey } from "@utils/graph"
import logger from "@utils/logger"
import Cookies from "js-cookie"
import { useSetAtom } from "jotai"
import { useCallback, useEffect, useRef } from "react"
import { nodesMapper } from "@features/builder/helpers/nodesMapper"

const DEFAULT_DEBOUNCE_MS = 1000

export const useNodesValidationWorker = ({
  nodes,
  graphDocumentData,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  cacheConfig,
}: {
  nodes: AppNode[]
  graphDocumentData: EditorGraphDto
  debounceMs?: number
  cacheConfig: WorkerCacheConfig
}) => {
  const setNodesValidation = useSetAtom(mergeNodesValidationAtom)
  const setNodesValidationRef = useRef(setNodesValidation)
  const workerRef = useRef<Worker | null>(null)
  const runIdRef = useRef(0)
  const nodesRef = useRef(nodes)
  const graphDocumentDataRef = useRef(graphDocumentData)

  useEffect(() => {
    setNodesValidationRef.current = setNodesValidation
  }, [setNodesValidation])

  useEffect(() => {
    nodesRef.current = nodes
  }, [nodes])

  useEffect(() => {
    graphDocumentDataRef.current = graphDocumentData
  }, [graphDocumentData])

  useEffect(() => {
    if (typeof Worker === "undefined") return
    const worker = new Worker(new URL("./nodesValidation.worker.ts", import.meta.url), {
      type: "module",
    })
    workerRef.current = worker

    const handleMessage = (event: MessageEvent<NodesValidationResponse>) => {
      const { runId, results } = event.data
      if (runId !== runIdRef.current) return
      const start = performance.now()

      const currentNodes = nodesRef.current
      const mapper = nodesMapper(currentNodes)

      const mergedResults: AllNodesValidationStates = {}
      for (const node of currentNodes) {
        const webworkerRes = results[node.id]
        mergedResults[node.id] = {
          ...webworkerRes,
          ...validateNodeExpression(node, mapper),
        }
      }
      const durationMs = performance.now() - start
      if (import.meta.env.VITE_SHOW_WORKER_VALIDATION_LOG === "true") {
        logger.info(
          `Validation runId:${event.data.runId} done in {mainThread: ${durationMs.toFixed(
            2,
          )}ms, workerThread: ${event.data.durationMs.toFixed(2)}ms}`,
        )
      }
      setNodesValidationRef.current(mergedResults)
    }

    worker.addEventListener("message", handleMessage)

    return () => {
      worker.removeEventListener("message", handleMessage)
      worker.terminate()
      workerRef.current = null
    }
  }, [])

  const runValidation = useCallback(() => {
    if (!workerRef.current) return

    const graphDataString = graphDocumentDataRef.current.data
    const graphData: GraphDetails | null = JSON.parse(graphDataString)
    const graphKey = graphData ? getNormalisedGraphKey(graphData) : ""

    runIdRef.current += 1

    workerRef.current.postMessage({
      runId: runIdRef.current,
      nodes: nodesRef.current,
      graphData: graphDataString,
      authToken: Cookies.get("User") ?? "",
      graphKey,
      apiUrl: import.meta.env.VITE_APP_API_URL as string,
      cacheConfig,
    } satisfies NodesValidationRequest)
  }, [cacheConfig])

  useSkipValidation({
    nodes,
    graphDocumentData,
    debounceMs,
    onValidation: runValidation,
  })
}
